import React, { memo, useCallback } from 'react';
import type { CSSProperties } from 'react';
import { mapOptionsListStyleToCSS } from '../../../../utils/styleMapper';
import { Image } from '../../Image';
import { OptionsItemText } from '../OptionsItemText';
import type { OptionsItemVariantProps } from './types';
import { getListImagePresentation } from './imageLayout';
import { getOptionItemA11yProps } from '../../../../utils/optionItemA11y';
import { resolveLabel } from '../../../../utils/contentResolver';
import styles from '../OptionsItem.module.css';

export const ImageTitle = memo(function ImageTitle({
  item,
  index,
  locale,
  variables,
  selectable,
  selected,
  disabled,
  selectionMode,
  appearance,
  selectedAppearance,
  onSelect,
  className,
  style,
  'data-testid': testId,
  editPath,
  previewMode,
}: OptionsItemVariantProps): React.ReactElement {
  const handleClick = useCallback(() => {
    if (selectable && !disabled && onSelect) {
      onSelect(item, index, !selected);
    }
  }, [selectable, disabled, onSelect, item, index, selected]);

  const activeStyle = selected ? selectedAppearance : appearance;
  const itemStyle: CSSProperties = mapOptionsListStyleToCSS(activeStyle);
  const imagePresentation = getListImagePresentation(item.image);

  // W7 (f): W1 writes honesty qualifiers ("Self-selected", "Self-reported") into
  // `items[*].subtitle`, and the four screens that carry them are authored `ImageTitle` —
  // which rendered the title only, so the qualifier never reached the reader. Render it
  // when the item actually has one; an item without a subtitle keeps its single-line
  // geometry exactly, which is what makes this safe at 320.
  const hasSubtitle = Boolean(resolveLabel(item.subtitle, locale, variables)?.trim());

  return (
    <div
      className={`${styles.listItem}${selected ? ` ${styles.selected}` : ''}${disabled ? ` ${styles.disabled}` : ''}${className ? ` ${className}` : ''}`}
      style={style ? { ...itemStyle, ...style } : itemStyle}
      onClick={selectable && !disabled ? handleClick : undefined}
      {...getOptionItemA11yProps({ selectable, selected, disabled, selectionMode, onActivate: handleClick })}
      data-testid={testId}
      data-edit-path={editPath}
    >
      <div className={styles.asset} style={imagePresentation.assetStyle}>
        <Image image={item.image} locale={locale} fillContainer={imagePresentation.fillContainer} previewMode={previewMode} editPath={editPath ? `${editPath}.image` : undefined} />
      </div>
      <div className={styles.content}>
        <OptionsItemText label={item.title} locale={locale} variables={variables} editPath={editPath ? `${editPath}.title` : undefined} />
        {hasSubtitle && (
          <OptionsItemText label={item.subtitle} locale={locale} variables={variables} editPath={editPath ? `${editPath}.subtitle` : undefined} />
        )}
      </div>
    </div>
  );
});

ImageTitle.displayName = 'ImageTitle';
